import { listContinuousMonitors } from '../../../api';
import type { ContinuousMonitorType } from '../../../api';
import { useResource } from '../../../hooks';
import { ErrorNotice, LoadingRows } from '../../../components/ui';
import { LevelBadge, QuietEmpty } from '../../../components/project/ui';
import type { Level } from '../../../components/project/projectHealth';
import { relativeTime } from '../../../components/project/projectHealth';
import { useProject } from '../ProjectContext';

const order: ContinuousMonitorType[] = [
  'wordpress_page',
  'wordpress_health',
  'wp_cron',
  'action_scheduler',
  'wordpress_errors',
  'wordpress_software',
  'tls_certificate',
];

const statusLevel: Record<string, Level> = {
  healthy: 'ok',
  warning: 'warning',
  critical: 'critical',
  unknown: 'unknown',
  disabled: 'unknown',
};

const statusWord: Record<string, string> = {
  healthy: 'Passing',
  warning: 'Warning',
  critical: 'Failing',
  unknown: 'Not checked yet',
  disabled: 'Off',
};

/**
 * MonitorHealthList is the Overview's one-line-per-check view of the
 * WordPress monitors of the selected environment: storefront pages,
 * WooCommerce health, WP-Cron, Action Scheduler, errors and updates. The
 * detail and history of each stay in Monitoring.
 */
export function MonitorHealthList() {
  const project = useProject();
  const environmentId = project.environment?.id;
  const monitors = useResource(
    () => listContinuousMonitors(project.projectId),
    [project.projectId, project.wordpressSite.data?.inspected_at],
    project.projectId,
  );

  const list = (monitors.data?.monitors ?? [])
    .filter((monitor) => monitor.environment_id === environmentId && order.includes(monitor.type))
    .sort((a, b) => order.indexOf(a.type) - order.indexOf(b.type) || a.name.localeCompare(b.name));

  if (monitors.loading && list.length === 0 && !monitors.error) return <LoadingRows label="Loading checks" rows={4} rowHeight="24px" />;
  // A failed read is not "no checks yet": say that the state is not known.
  if (monitors.error && list.length === 0) {
    return <ErrorNotice error={monitors.error} onRetry={monitors.reload} nextStep="The state of the shop checks could not be read, so none is shown as passing." />;
  }
  if (list.length === 0) {
    return (
      <QuietEmpty testId="no-wp-monitors">No checks yet</QuietEmpty>
    );
  }

  return (
    <ul className="status-list" data-testid="wp-monitor-list">
      {list.map((monitor) => (
        <li key={monitor.id} className="status-list__item">
          <span className="status-list__name">{monitor.name}</span>
          <span className="status-list__detail muted">
            {monitor.enabled ? (monitor.last_run_at ? `checked ${relativeTime(monitor.last_run_at)}` : 'not run') : 'off'}
          </span>
          <LevelBadge level={statusLevel[monitor.status] ?? 'unknown'}>{statusWord[monitor.status] ?? monitor.status}</LevelBadge>
        </li>
      ))}
    </ul>
  );
}
